import { TabUserManager } from "./tab-user-manager";
import { getAccessTokenFor, requestToken, requestTokenFor } from "./tab-auth";
import { resolveAuthority, RESPONSE_MODE, SCOPE } from "./sts";
import { claimTabChain, renewSettingsFor } from "./tab-chain";
import { extensionsOutcome, startSignIn, showFailure, accessTokenCookie } from "./startup";

// Anything that stops the app loading shows a message and a way back, never a blank page.
// The retry link drops the fragment so clicking it reloads instead of just scrolling.
const fail = (message, err) => {
    console.error(err);
    showFailure(document, message, window.location.origin + window.location.pathname + window.location.search);
};

(async function () {
    // Safari restores a back/forward-cache page without its chain lock, so reload to claim the chain again.
    window.addEventListener("pageshow", (event) => {
        if (!event.persisted) return;
        // Stop the scheduled renew so the restored page can't start one before the reload lands.
        window.$tp.auth.userManager?.stopSilentRenew();
        window.location.reload();
    });

    const { mgr: userManager, claim } = await (async function () {
        ///////////////////////////////
        // OidcClient config
        ///////////////////////////////
        const hashedHost = $tp.hashedHost;
        var stsUrl = resolveAuthority(window.$tp.auth.endpoint, window.location.host);
        let clientId = window.location.host;
        let pathname = window.location.pathname;
        let query = window.location.search;
        switch (hashedHost) {
            case 'a1136870194':
            case 'a1312413555':
                clientId = localStorage.getItem("mobile_client_id");
                if (!!clientId) {
                    if (clientId != window.location.host) {
                        window.location.href = `https://${clientId}${pathname}${query}`;
                    }
                    else {
                        clientId = window.location.host;
                    }
                }
                else {
                    localStorage.setItem('redirect_path', pathname)
                    localStorage.setItem('redirect_query', query);
                    window.location.href = `https://${window.location.host}/mobile-redirect.html`;
                }
                break;
            default:
                break;
        }

        window.$tp.auth.endpoint = stsUrl;
        // clientId is final here. A duplicated tab must not renew with the refresh token it copied, so the claim
        // decides the renewal settings. A framed page shares its parent's chain and claims nothing.
        const claim = await claimTabChain({
            storage: sessionStorage,
            locks: navigator.locks,
            clientId,
            framed: window.self !== window.top,
        });
        const config = {
            client_id: clientId,
            redirect_uri:
                window.location.protocol + '//' + clientId + '/callback.html',
            post_logout_redirect_uri:
                window.location.protocol + '//' + clientId + '/',
            // PKCE is automatic in oidc-client-ts for response_type: 'code'.
            response_type: 'code',
            response_mode: RESPONSE_MODE,
            scope: SCOPE,
            requestTimeoutInSeconds: 10,
            authority: window.$tp.auth.endpoint,
            silent_redirect_uri:
                window.location.protocol +
                '//' +
                clientId +
                '/silentrefreshframe.html',
            ...renewSettingsFor(claim),
            acr_values: 'tenant',
        };
        var mgr = new TabUserManager(config);
        if (claim === "framed") mgr.disableRenewal();

        mgr.events.addUserLoaded((user) => {
            mgr.getUser().then((user) => {
                if (!!user) {
                    const token = user.access_token;
                    document.cookie = accessTokenCookie(token);
                }
            });
        });

        return { mgr, claim };
    })();

    // $tp.auth is created in the index.html file
    // $tp is a ui.core global that, by default, is an empty object - this can be used to provide globally available functionality
    // do not abuse
    $tp.auth.userManager = userManager;
    $tp.auth.requestToken = () => requestToken(userManager);
    $tp.auth.getAccessToken = () => getAccessTokenFor(window, userManager);
    
    const initialize = () => {
        const addFile = (element, type, source) => {
            const head = document.getElementsByTagName(element)[0];
            switch (type) {
                case 'script':
                    let script = document.createElement('script');
                    script.type = 'text/javascript';
                    script.src = source;
                    head.appendChild(script);
                    break;
                case 'link':
                    let link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = source;
                    head.appendChild(link);
                    break;
                default:
                    break;
            }

        }
        addFile("head", "link", "https://cdn.ikaun.com/files/bootstrap/bootstrap-flex.min.css");
        addFile("head", "link", $tp.assets.theme);
        addFile("body", "script", $tp.assets.bundle);
        addFile("body", "script", "https://cdn.ikaun.com/files/moment/moment.min.js");
        addFile("body", "link", "https://cdn.ikaun.com/files/dhx/diagram.min.css");
        addFile("body", "script", "https://cdn.ikaun.com/files/dhx/diagram.min.js")
        addFile("body", "script", "https://kit.fontawesome.com/f32e831a56.js");
        addFile("body", "link", "https://fonts.googleapis.com/css?family=Open+Sans:300,300i,400,400i,600,600i,700,700i,800,800i|Roboto:100,100i,300,300i,400,400i,500,500i,700,700i,900,900i");
        addFile("body", "link", "https://fonts.googleapis.com/css?family=Montserrat:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i");
        addFile("body", "link", "https://fonts.googleapis.com/css?family=Source+Sans+Pro");
        addFile("body", "link", "https://fonts.googleapis.com/css?family=Ubuntu:300,700");
        addFile("body", "link", "https://fonts.googleapis.com/css?family=Playfair+Display:400,400i,700,700i,900,900i");
        addFile("body", "link", "https://fonts.googleapis.com/css?family=Raleway:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i");
        addFile("body", "link", "/static/content/fonts/blokkfont.css");
    }

    const signIn = () => {
        //set cookie for redirect after authentication
        localStorage.setItem('redirect_url', window.location.href);
        startSignIn($tp.auth.userManager, (err) => fail("Sign-in failed.", err));
    };

    if (claim === "duplicate") {
        // The copied refresh token belongs to the original tab: drop it and sign in for a chain of our own.
        try {
            await userManager.removeUser();
        } catch (err) {
            console.error(err);
        }
        signIn();
        return;
    }

    // A token that expired while the tab slept is renewed by refresh token before falling back to sign-in.
    requestTokenFor(window, userManager).then(token => {
        if (!token) {
            signIn();
        }
        else {
            // userLoaded doesn't fire on page reload; set cookie explicitly
            // so server routes (e.g. /files/* image proxy) keep working.
            document.cookie = accessTokenCookie(token);
            var xhttp = new XMLHttpRequest();
            xhttp.onload = function () {
                //If 401, redirect to sts, if 200 load bundle.js, anything else says so
                switch (extensionsOutcome(this.status)) {
                    case "signin":
                        signIn();
                        break;
                    case "load":
                        try {
                            window.ikaun_extensions = JSON.parse(this.response);
                        } catch (err) {
                            fail("This page didn't load.", err);
                            break;
                        }
                        initialize();
                        break;
                    default:
                        fail("This page didn't load.", new Error(`/extensions returned ${this.status}`));
                        break;
                }
            };
            xhttp.onerror = function () {
                fail("This page didn't load.", new Error("/extensions request failed"));
            };
            xhttp.open("GET", `${$tp.api.endpoint}/extensions`, true);
            xhttp.setRequestHeader("Authorization", `bearer ${token}`);
            xhttp.send();
        }
    }).catch((err) => fail("This page didn't load.", err));
})().catch((err) => fail("This page didn't load.", err));